import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  // Bell,
  // Calendar,
  Contact,
  // Eye,
  Flow,
  Folder,
  Home,
  // Inbox,
  // Outreach,
  Reports,
  Search,
  Settings,
  Templates,
  Circles,
  Interview,
  // Stack,
} from '@/assets/icons/nav-v2';
import {
  Atom01,
  InternalJobs as InternalJobsIcon,
  LayersThree02,
  UsersRight,
} from '@kula-ai/icons-core';
import { NewAppNotification } from '@/components/notifications';
import { getReviews, reviewsApiActions } from '@/store/reviews';
import { whenIdle } from '@/utils/whenIdle';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import {
  NavLink,
  AskKulaNavLink,
  TaskNav,
  NavSearch,
  NavProfile,
  Divider,
} from './components'; // add NavMenu component for secondary menu
import { useIsExternalUser } from '@/hooks/useIsExternalUser';
import { usePermission } from '@/ui/permission';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import { HelpSupport } from './help-and-support';
import { useKFlags } from '@/hooks/use-k-flags';
import { selectCurrUser } from '@/store/bootstrap/selectors';

export const useNavigationConfig = () => {
  const { ats_enabled, feNoteTaker, feAgentForgeV1, internalJobsPost } =
    useKFlags();
  const { isCirclesEnabled, isFlowEnabled, isReportsEnabled } =
    usePermissionGuard();
  const dispatch = useDispatch();
  const {
    navigationReviews: {
      data: { data: { metrics_count: { awaiting_my_review } = {} } = {} } = {},
    },
  } = useSelector(getReviews);

  const isContactsEnabled = usePermission({
    permission: 'circles.contacts?',
  });
  const {
    isJobsVisible,
    isReportsEnabledForAtsUsers,
    requisitionsPermissions,
    // AgentForge access uses the job-creation permission.
    isJobCreateAllowed: canAccessAgentForge,
  } = useAtsPermissions();

  const isExternalUser = useIsExternalUser();

  // const isCommTemplatesEnabledForAts = usePermission({
  //   permission: 'api_ats_v1_ats_comm_template.create?',
  // });

  const currUser = useSelector(selectCurrUser);

  const isRequisitionsEnabled =
    currUser?.requisition?.enabled && requisitionsPermissions.index;

  const isJobsOrRequisitionsActive =
    window.location.pathname.startsWith('/jobs') ||
    window.location.pathname.startsWith('/requisitions');

  useEffect(() => {
    // Use separate reducer key for navigation data to avoid race condition with InternalReviewsList
    //
    // Deferred to idle: this feeds a count badge in the nav, not anything on
    // first paint, and it ran during page load on every route. Sentry, 24h,
    // settings routes alone: 170 calls at p75 1,564ms — 336k ms a day in front
    // of page content. `whenIdle` returns its own canceller, which is the
    // cleanup this effect needs.
    return whenIdle(() => {
      dispatch(reviewsApiActions('fetchNavigationReviews'));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(() => {
    const navItemsTopSection = [
      {
        component: NavSearch,
        props: { title: 'Search', icon: Search },
        styles: { my: '12px' },
        isEnabled: !ats_enabled,
      },
      {
        component: NewAppNotification,
        // `match` lets the side nav detect the selected item for the notch, since
        // these render their own link internally (no top-level `props.link`).
        match: '/notifications',
        isEnabled: true,
        isAllowedToExternalUsers: true,
      },
      {
        component: TaskNav,
        match: '/tasks',
        isEnabled: isFlowEnabled || ats_enabled,
      },
      {
        component: Divider,
        props: {},
        styles: {
          borderBottom: '1px solid',
          borderBottomColor: 'secondary-100',
          my: '12px',
        },
        isEnabled: true,
        isAllowedToExternalUsers: true,
      },
    ];

    const navItemsMiddleSection = [
      {
        // Wrapped so the one-time "Meet Ask Kula" launch announcement anchors
        // to this item; falls back to a plain NavLink once dismissed.
        component: AskKulaNavLink,
        props: {
          link: '/ask-kula',
          title: 'Ask Kula',
          icon: Atom01,
          testID: 'agents',
          nudgeEnabled: feAgentForgeV1 && Boolean(canAccessAgentForge),
        },
        isEnabled: feAgentForgeV1 && Boolean(canAccessAgentForge),
      },
      {
        component: NavLink,
        props: {
          link: '/dashboard',
          title: 'Dashboard',
          icon: Home,
          testID: 'dashboard',
        },
        isEnabled: true, // all
      },
      {
        component: NavLink,
        props: {
          link: '/jobs',
          title: isRequisitionsEnabled ? 'Jobs & Requisitions' : 'Jobs',
          icon: LayersThree02,
          testID: 'jobs',
          // route is jobs or requisitions, so we need to show active state for both
          isCustomActive: isJobsOrRequisitionsActive,
        },
        // Todo-Req: Add permission check for requisitions later
        isEnabled: isJobsVisible && ats_enabled,
      },
      // {
      //   component: NavMenu,
      //   props: {
      //     // link: '/flows',
      //     title: 'Outreach',
      //     icon: Flow,
      //     menuItem: outReachItems,
      //   },
      //   isEnabled: isFlowEnabled,
      // },
      {
        component: NavLink,
        props: {
          link: '/flows',
          title: 'Flows',
          icon: Flow,
          testID: 'flows',
        },
        isEnabled: isFlowEnabled,
      },
      {
        component: NavLink,
        props: {
          link: '/projects',
          title: 'Projects',
          icon: Folder,
          count: awaiting_my_review,
          testID: 'projects',
        },
        isEnabled: isFlowEnabled || isExternalUser,
        isAllowedToExternalUsers: true,
      },

      {
        component: NavLink,
        props: {
          link: '/circles',
          title: 'Circles',
          icon: Circles,
          testID: 'circles',
        },
        isEnabled: isCirclesEnabled,
      },
      {
        component: NavLink,
        props: {
          link: '/candidates',
          title: 'Candidates',
          icon: Contact,
          testID: 'contacts',
        },
        isEnabled: isFlowEnabled || isContactsEnabled,
      },
      {
        component: NavLink,
        props: {
          link: '/interviews/upcoming',
          title: 'Interviews',
          icon: Interview,
          testID: 'interviews',
        },
        isEnabled: feNoteTaker || ats_enabled, // all can see
      },
      {
        component: NavLink,
        props: {
          link: '/templates',
          title: 'Templates',
          icon: Templates,
          testID: 'templates',
        },
        isEnabled: !ats_enabled && isFlowEnabled, // Only show when ats_enabled is false
      },
      {
        component: NavLink,
        props: {
          link: ats_enabled ? '/reports' : '/reports-home',
          title: 'Reports and Analytics',
          icon: Reports,
          testID: 'reports',
        },
        isEnabled:
          (isReportsEnabled && isFlowEnabled) || isReportsEnabledForAtsUsers,
      },
      {
        component: NavLink,
        props: {
          link: '/referrals',
          title: 'Referrals',
          icon: UsersRight,
          testID: 'referrals',
        },
        isEnabled: ats_enabled, // For all if ats enabled
      },
      {
        component: NavLink,
        props: {
          link: '/internal-job-board',
          title: 'Internal job board',
          icon: InternalJobsIcon,
          testID: 'internal-job-board',
        },
        isEnabled: internalJobsPost && ats_enabled, // Gated behind internalJobsPost flag
      },
    ];

    const navBottomItems = [
      {
        component: Divider,
        styles: {
          borderBottom: '1px solid',
          borderBottomColor: 'secondary-100',
          my: '12px',
        },
        props: {},
        isEnabled: isFlowEnabled,
      },
      {
        component: HelpSupport,
        isEnabled: ats_enabled,
      },
      {
        component: NavLink,
        props: {
          link: ats_enabled ? '/settings' : '/settings/profile/general',
          title: 'Settings',
          icon: Settings,
          testID: 'settings',
        },
        isEnabled: true,
      },
      {
        component: NavProfile,
        isEnabled: !ats_enabled,
        isAllowedToExternalUsers: true,
      },
    ];

    return {
      navItemsTopSection,
      navItemsMiddleSection,
      navBottomItems,
    };
  }, [
    ats_enabled,
    isFlowEnabled,
    feAgentForgeV1,
    canAccessAgentForge,
    isRequisitionsEnabled,
    isJobsOrRequisitionsActive,
    isJobsVisible,
    isExternalUser,
    awaiting_my_review,
    isCirclesEnabled,
    isContactsEnabled,
    feNoteTaker,
    isReportsEnabled,
    isReportsEnabledForAtsUsers,
    internalJobsPost,
  ]);
};
